/**
 * Design tokens of the locked v1 design (design-handoff/02 §1). Values are exact; names are ours.
 * The same values are exposed as CSS custom properties on the stage (base.module.css).
 */

export const color = {
  ground: '#0d1018',
  groundLift: '#171c29',
  tile: '#1a2030',
  tileHover: '#262d3f',
  tileToday: '#1f2636',
  tileTodayHover: '#232a3b',
  tileFuture: '#141925',
  tileLoading: '#161b28',
  mediaBrokenBg: '#151a26',
  buttonDark: '#10141d',
  panel88: 'rgba(26, 32, 48, 0.88)',
  panel92: 'rgba(26, 32, 48, 0.92)',
  panel94: 'rgba(26, 32, 48, 0.94)',
  panel96: 'rgba(26, 32, 48, 0.96)',
  panel97: 'rgba(26, 32, 48, 0.97)',
  textStrong: '#e8ebf2',
  text: '#c9cfdc',
  textMuted: '#a3abc0',
  textFaint: '#7d86a0',
  textPlaceholder: '#59627a',
  textStatus: '#d5dae5',
  ink: '#0d1018',
  inkOnLight: '#141821',
  inkOnLightEmpty: 'rgba(20, 24, 33, 0.5)',
  white: '#ffffff',
  paper: '#e9edf5',
  line: '#3b4358',
  lineStrong: '#8a93ab',
  focus: '#7aa7ff',
  danger: '#f2a093',
  dangerStrong: '#e0685a',
  dangerTint: 'rgba(224, 104, 90, 0.08)',
} as const;

/** `rgba(233, 237, 245, a)`: the paper white at an alpha. */
export const paperA = (a: number): string => `rgba(233, 237, 245, ${a})`;
/** `rgba(255, 255, 255, a)`. */
export const whiteA = (a: number): string => `rgba(255, 255, 255, ${a})`;
/** `rgba(13, 16, 24, a)`: the ground at an alpha. */
export const groundA = (a: number): string => `rgba(13, 16, 24, ${a})`;

/** The eight colours a member can pick, in Look order (02 §1.3). */
export const palette = ['#f0a26e', '#6aaef0', '#ee8fb8', '#86d4a0', '#b3a4f5', '#e8c75f', '#7fd4d0', '#f08a7e'] as const;

const paletteNames: Readonly<Record<string, string>> = {
  '#f0a26e': 'orange', '#6aaef0': 'blue', '#ee8fb8': 'pink', '#86d4a0': 'green',
  '#b3a4f5': 'lavender', '#e8c75f': 'yellow', '#7fd4d0': 'teal', '#f08a7e': 'coral',
};
/** `colourName` of the prototype: the palette name, or "colour" for anything else. */
export const colourName = (hex: string): string => paletteNames[hex] ?? 'colour';

export const font = {
  grotesk: "'Schibsted Grotesk', 'Helvetica Neue', sans-serif",
  groteskPill: "'Schibsted Grotesk', sans-serif",
  /** Labels, numbers and status lines (owner, 4 October 2026: Montserrat in place of IBM Plex Mono). */
  label: "'Montserrat', sans-serif",
} as const;

export const shadow = {
  island: '0 0 0 1px rgba(255, 255, 255, 0.06), 0 14px 34px rgba(0, 0, 0, 0.4)',
  tray: '0 0 0 1px rgba(255, 255, 255, 0.08), 0 14px 34px rgba(0, 0, 0, 0.4)',
  popover: '0 0 0 1px rgba(255, 255, 255, 0.08), 0 12px 28px rgba(0, 0, 0, 0.4)',
  menuItem: '0 0 0 1px rgba(255, 255, 255, 0.08), 0 12px 28px rgba(0, 0, 0, 0.45)',
  pill: '0 0 0 1px rgba(255, 255, 255, 0.08), 0 10px 24px rgba(0, 0, 0, 0.35)',
  toast: '0 12px 30px rgba(0, 0, 0, 0.25)',
  fab: '0 8px 22px rgba(0, 0, 0, 0.4)',
  avatar: '0 8px 20px rgba(0, 0, 0, 0.35)',
  dayCircle: '0 6px 18px rgba(0, 0, 0, 0.35)',
  liftedWidget: '0 18px 40px rgba(0, 0, 0, 0.45)',
  grip: '0 1px 4px rgba(0, 0, 0, 0.6)',
  todayHalo: '0 0 30px 4px rgba(233, 237, 245, 0.13)',
  todayHaloHover: '0 0 46px 12px rgba(233, 237, 245, 0.24)',
  tileGlow: '0 0 18px 2px rgba(233, 237, 245, 0.28)',
  tileGlowRest: '0 0 0 0 rgba(233, 237, 245, 0)',
} as const;

/**
 * Strength of the colour glow behind the top of a profile: the member colour at this alpha in the
 * centre, fading to nothing. The prototype has 0.16; the owner asked for it to be "more subtle"
 * (2 October 2026). Half of the prototype's value is this build's choice; say if it should move.
 */
export const PROFILE_GLOW_ALPHA = 0.08;

/** Layer order of the stage (02 §1.10). */
export const layer = {
  calendar: 1, day: 2, header: 4, surface: 5, profileHeader: 6, island: 9, hub: 10, toast: 40,
  widget: 1, widgetSelected: 4, widgetDragged: 8, toolbar: 20, swatches: 21,
} as const;

/** Stage and grid geometry at 1440 × 900 (02 §2). */
export const geo = {
  stageW: 1440, stageH: 900, centreX: 720,
  gridX: 440, gridY: 236, gridW: 560, gridH: 478,
  cell: 68, gap: 14, step: 82, cols: 7, rows: 6,
  islandTop: 804, islandH: 64,
} as const;

export const ease = {
  out: 'cubic-bezier(.2,.8,.2,1)',
  pop: 'cubic-bezier(.3,1.6,.5,1)',
  popSoft: 'cubic-bezier(.3,1.5,.5,1)',
  inOut: 'cubic-bezier(.4,0,.2,1)',
  clipOpen: 'cubic-bezier(.7,0,.2,1)',
  overshoot: 'cubic-bezier(.34,1.3,.52,1)',
} as const;

/** Inline SVG path data, all on a 24 × 24 view box (02 §1.11). */
export const icon = {
  home: 'M4 11.2L12 4.5l8 6.7M6.5 9.6V19.5h11V9.6M10.2 19.5v-4.8h3.6v4.8',
  chevronLeft: 'M15 6l-6 6 6 6',
  chevronRight: 'M9 6l6 6-6 6',
  plus: 'M12 5v14M5 12h14',
  pencil: 'M4 20h4L18.5 9.5l-4-4L4 16v4zM13 7l4 4',
  pencilBody: 'M4 20h4L18.5 9.5l-4-4L4 16v4z',
  pencilTip: 'M13 7l4 4',
  up: 'M12 19V5M6 11l6-6 6 6',
  uploadLook: 'M12 16V4M7 9l5-5 5 5M4 20h16',
  uploadArrow: 'M12 15V4M7.5 8.5L12 4l4.5 4.5',
  uploadTray: 'M4 14v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4',
  media: 'M4 5h16v14H4zM4 16l5-5 4 4 3-3 4 4',
  text: 'M5 6h14M12 6v13M9 19h6',
  song: 'M9 18V6l10-2v12M9 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM19 16a2 2 0 1 1-4 0 2 2 0 0 1 4 0z',
  minus: 'M6 12h12',
  plusSmall: 'M12 6v12M6 12h12',
  remove: 'M5 7h14M10 7V5h4v2M7 7l1 12h8l1-12',
  play: 'M8 5.5v13l11-6.5z',
  pause: 'M7 5h3.5v14H7zM13.5 5H17v14h-3.5z',
  /** Not from the prototype (owner, 4 October 2026): "View your post", the camera and record items and the video sound toggle. */
  eye: 'M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  camera: 'M4 8h3.2l1.6-2.5h6.4L16.8 8H20v11H4zM12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  record: 'M3.5 7h11.5v10H3.5zM15 10.5l5.5-3v9l-5.5-3',
  sound: 'M4 9.5h3.5L12 5.5v13l-4.5-4H4zM15.5 9a4 4 0 0 1 0 6M18 6.5a7.5 7.5 0 0 1 0 11',
  muted: 'M4 9.5h3.5L12 5.5v13l-4.5-4H4zM16 9.5l5 5M21 9.5l-5 5',
} as const;
